import { useState, useCallback } from "react";
import { MobileScopeNote } from "./MobileScopeNote.tsx";
import { PersonBadge } from "../dashboard/PersonBadge.tsx";
import { EstimateStatusBadge } from "../dashboard/EstimateStatusBadge.tsx";
import { DateBadge } from "../dashboard/DateBadge.tsx";
import type { AddNoteBody, Estimation, File } from "@/interfaces/estimation.interface.ts";
import { Button } from "../ui/button.tsx";
import { ArrowLeft, Loader2, Paperclip } from "lucide-react";
import { useParams } from "react-router-dom";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { useDispatch } from "@/redux/store";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useValidatePDFExportMutation, useGeneratePDFPreviewMutation, useExportPDFMutation } from "@/hooks/mutation/pdf-export";
import FixMissingDimensionsModal from "./FixMissingDimensionsModal";
import { WarningAlert } from "@/components/modals/WarningAlert";
import { toast } from "sonner";
import type { MissingDimensionInfo, DimensionInfo } from "@rebuild/shared";
import type { File as EstimateFileType } from "@/interfaces/estimation.interface";

type MobileExportStep =
  | { type: "idle" }
  | {
      type: "missing_dimensions";
      missingDimensions: MissingDimensionInfo[];
    };

type MobileEstimationDetailProps = {
  estimation?: Estimation;
  onBack?: () => void;
  onAddNotePress?: () => void;
};

export const MobileEstimationDetail = ({ estimation, onBack, onAddNotePress }: MobileEstimationDetailProps) => {
  const { estimateId } = useParams();

  const { isFeatureEnabled } = useFeatureFlags();
  const isPdfExportEnabled = isFeatureEnabled("PDF_EXPORT_ENABLED");

  const { mutateAsync: validatePDFExport, isPending: isValidating } = useValidatePDFExportMutation();
  const { mutateAsync: generatePDFPreview, isPending: isGeneratingPreview } = useGeneratePDFPreviewMutation();
  const { mutateAsync: exportPDF, isPending: isExporting } = useExportPDFMutation();

  const [exportStep, setExportStep] = useState<MobileExportStep>({ type: "idle" });
  const [showPdfExportRequiredFieldsAlert, setShowPdfExportRequiredFieldsAlert] = useState(false);

  const { isUpdateScopeNoteLoading, estimationDetail } = useEstimateDetail(estimateId!);
  const dispatch = useDispatch();

  const isScopeNoteBeingProcessed = isUpdateScopeNoteLoading || !estimation?.autoMergeRoomCompleted;
  const isPdfLoading = isValidating || isGeneratingPreview || isExporting;

  const performExport = useCallback(
    async (dimensionsData: DimensionInfo[]) => {
      if (!estimateId) return;

      try {
        // Generate preview data first
        const previewData = await generatePDFPreview({
          estimateId,
          pricingAndDimensionInfo: { pricing: [], dimension: dimensionsData },
          verbose: false,
          clearCache: false,
        });

        // Export PDF
        const pdfBytes = await exportPDF({
          estimateId,
          pdfPreviewData: previewData,
        });

        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const filename = estimation?.projectName ? `${estimation.projectName} - Estimate Proposal.pdf` : "Estimate Proposal.pdf";

        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);

        toast.success("PDF export completed");
        setExportStep({ type: "idle" });
      } catch {
        toast.error("Failed to export PDF");
      }
    },
    [estimateId, estimation?.projectName, generatePDFPreview, exportPDF],
  );

  const handlePDFExport = useCallback(async () => {
    if (!estimateId) return;

    try {
      const validationResult = await validatePDFExport(estimateId);

      if (validationResult.requiresOfficeSelection) {
        setShowPdfExportRequiredFieldsAlert(true);
        return;
      }

      const hasMissingDimensions = validationResult.dimension.length > 0;

      if (hasMissingDimensions) {
        setExportStep({
          type: "missing_dimensions",
          missingDimensions: validationResult.dimension,
        });
        return;
      }

      // No missing data - proceed directly to export on mobile
      await performExport([]);
    } catch {
      toast.error("Failed to validate PDF export", {
        className: "!text-red-500 !bg-red-500/10 !border-red-500",
      });
    }
  }, [estimateId, validatePDFExport, performExport]);

  const handleContinueWithDimensions = useCallback(
    async (dimensionsData: DimensionInfo[]) => {
      if (exportStep.type !== "missing_dimensions") return;

      // Optimistically sync the entered dimensions into estimation.measurementData
      // so the per-room metric cells reflect the new values right away. The
      // backend /pdfPreviewData endpoint persists the same merge.
      if (estimation && estimationDetail) {
        const roomNameToPageId = new Map<string, string>();
        for (const room of estimationDetail) {
          const pageId = room.dataForUI.metadata.pageId;
          const roomName = room.dataForUI.metadata.roomName;
          if (pageId && roomName) roomNameToPageId.set(roomName, pageId);
        }

        const merged = [...(estimation.measurementData ?? [])];
        for (const dim of dimensionsData) {
          const pageId = roomNameToPageId.get(dim.roomName);
          if (!pageId) continue;
          const quantity = typeof dim.quantity === "number" ? dim.quantity : Number(dim.quantity);
          if (isNaN(quantity)) continue;
          const acronym = dim.acronym.toLowerCase();
          const idx = merged.findIndex((e) => e.id === pageId);
          if (idx === -1) {
            merged.push({ id: pageId, measurement: { [acronym]: quantity } });
          } else {
            merged[idx] = {
              ...merged[idx],
              measurement: { ...merged[idx].measurement, [acronym]: quantity },
            };
          }
        }

        dispatch(
          estimationActions.setEstimationState({
            estimation: { ...estimation, measurementData: merged } as Estimation,
          }),
        );
      }

      await performExport(dimensionsData);
    },
    [exportStep, performExport, estimation, estimationDetail, dispatch],
  );

  const resetExportStep = useCallback(() => {
    setExportStep({ type: "idle" });
  }, []);

  const notes =
    estimation && estimation.scopeNoteObjects
      ? [...estimation.scopeNoteObjects].sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
      : [];

  return (
    <div className="h-screen w-screen overflow-y-auto bg-[#f8f9fa] p-4">
      <div className="flex w-full items-center justify-between">
        <Button
          onClick={onBack}
          className="rounded-lg! bg-black! px-4 py-4! text-sm leading-6! font-medium text-white"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        {isPdfExportEnabled && (
          <Button
            variant="default"
            onClick={!isScopeNoteBeingProcessed ? handlePDFExport : undefined}
            disabled={isScopeNoteBeingProcessed || isPdfLoading}
            className={`flex items-center gap-2 rounded-lg! px-4 py-2 text-sm! leading-6 font-medium! ${
              !isScopeNoteBeingProcessed ? `${isPdfLoading ? "animate-pulse" : ""}` : "cursor-not-allowed bg-gray-400 text-gray-200"
            }`}
          >
            {(isScopeNoteBeingProcessed || isPdfLoading) && <Loader2 className="h-4 w-4 animate-spin" />}
            {isScopeNoteBeingProcessed ? "Processing" : isPdfLoading ? "Exporting..." : "Export to PDF"}
          </Button>
        )}
      </div>

      <div className="mt-4 flex w-full flex-col rounded-xl border border-gray-200 bg-white p-5">
        <div className="project-name mb-2 text-xl font-bold text-gray-900">{estimation?.projectName ?? ""}</div>
        <div className="project-reference mb-1 text-sm text-gray-400">Reference: #{estimation?.estimateReferenceNumber ?? ""}</div>
        <div className="project-address mb-3 text-sm text-gray-700">{estimation?.propertyAddress ?? ""}</div>
        <div className="project-meta flex flex-wrap items-center gap-4">
          <EstimateStatusBadge status={estimation?.status} />
          <PersonBadge id={estimation?.assignee || ""} />
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">Created by</span>
            <PersonBadge id={estimation?.createdByUserId} emptyLabel="—" unresolvedLabel="Unknown" />
          </div>
        </div>
      </div>

      {/* pb-16 below is important for some iOS devices that don't include the safari bar in h-screen */}
      <div className="mt-4 flex w-full flex-col pb-16">
        <div className="mb-4 flex w-full items-center justify-between">
          <div className="text-lg font-semibold text-gray-900">Session Notes</div>
          <button
            className={`${isScopeNoteBeingProcessed ? "bg-gray-200!" : "bg-gray-900!"} py-2! text-white`}
            onClick={onAddNotePress}
            disabled={isScopeNoteBeingProcessed}
          >
            + Add Note
          </button>
        </div>
        <MobileActivityTimelineList notes={notes} files={estimation?.files || []} estimateId={estimation?.id || ""} />
      </div>
      <FixMissingDimensionsModal
        open={exportStep.type === "missing_dimensions"}
        onClose={resetExportStep}
        onContinue={handleContinueWithDimensions}
        missingDimensions={exportStep.type === "missing_dimensions" ? exportStep.missingDimensions : []}
      />
      <WarningAlert
        title="The following fields are required to export to PDF:"
        isOpen={showPdfExportRequiredFieldsAlert}
        errors={["Office"]}
        onCloseAlert={() => setShowPdfExportRequiredFieldsAlert(false)}
      />
    </div>
  );
};

type ActivityTimelineItem = {
  id: string;
  type: "note" | "file";
  dateCreated: string;
  data: AddNoteBody | File;
};

const MobileActivityTimelineList = ({
  notes,
  files,
  estimateId,
}: {
  notes: AddNoteBody[];
  files: EstimateFileType[];
  estimateId: string;
}) => {
  // Combine notes and files into timeline items
  const timelineItems: ActivityTimelineItem[] = [
    ...notes.map((note) => ({
      id: `note_${note.uuid}`,
      type: "note" as const,
      dateCreated: note.dateCreated,
      data: note,
    })),
    ...files.map((file) => ({
      id: `file_${file.uploadId}`,
      type: "file" as const,
      dateCreated: file.uploadDate!,
      data: file,
    })),
  ];

  // Sort by date (newest first)
  const sortedItems = timelineItems.sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());

  if (sortedItems.length === 0) {
    return (
      <div className="py-10 text-center text-gray-400">
        <div className="mb-2 text-base">No documentation uploaded yet.</div>
        <div className="text-sm opacity-80">Add documentation to update estimate.</div>
      </div>
    );
  }

  return (
    <>
      {sortedItems.map((item, index) => {
        if (item.type === "note") {
          const note = item.data as AddNoteBody;
          const isFirstNote = sortedItems.slice(0, index + 1).filter((i) => i.type === "note").length === 1;
          return <MobileScopeNote key={item.id} estimateId={estimateId} note={note} editable={isFirstNote} />;
        } else {
          const file = item.data as File;
          return <UploadedFileMobile key={item.id} file={file} />;
        }
      })}
    </>
  );
};

const UploadedFileMobile = ({ file }: { file: EstimateFileType }) => {
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileExtension = (filename: string) => {
    return filename.split(".").pop()?.toLowerCase() || "";
  };

  const getFileTypeInfo = (filename: string) => {
    const extension = getFileExtension(filename);

    if (extension === "csv" || extension === "xlsx") {
      return {
        type: extension === "csv" ? "CSV Spreadsheet" : "Excel Spreadsheet",
        bgColor: "bg-green-50",
        borderColor: "border-green-300",
        iconColor: "text-green-600",
      };
    } else if (extension === "pdf") {
      return {
        type: "PDF Document",
        bgColor: "bg-gray-50",
        borderColor: "border-gray-300",
        iconColor: "text-gray-600",
      };
    } else {
      return {
        type: `${extension.toUpperCase()} File`,
        bgColor: "bg-gray-50",
        borderColor: "border-gray-300",
        iconColor: "text-gray-600",
      };
    }
  };

  const fileTypeInfo = getFileTypeInfo(file.originalname);

  return (
    <div className="mb-3 rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <DateBadge date={file.uploadDate} showTime={true} />
      </div>
      <div className="mt-2">
        <div className={`flex items-center gap-3 rounded-lg border p-3 ${fileTypeInfo.bgColor} ${fileTypeInfo.borderColor}`}>
          <Paperclip size={20} className={`${fileTypeInfo.iconColor} shrink-0`} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-gray-900">{file.originalname}</div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>{fileTypeInfo.type}</span>
              <span>•</span>
              <span>{formatFileSize(file.size || 0)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
